import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Grid,
  Alert,
  Snackbar
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { ContactEnum } from '../../utils/ContactEnum';
import { postLandingContact } from '../../pages/Landing/landing.api';
import { useTranslation } from 'react-i18next';

interface QuoteModalProps {
  open: boolean;
  onClose: () => void;
  type: ContactEnum;
  onSubmit?: (data: { name: string; email: string; companyName: string; description: string }, type: ContactEnum) => void;
}

const QuoteModal: React.FC<QuoteModalProps> = ({ open, onClose, type, onSubmit }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    companyName: '',
    description: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [apiErrors, setApiErrors] = useState<string[]>([]);
  // Add dynamic flag for support vs budget texts
  const isSupport = type === ContactEnum.SUPPORT;

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('quote_modal_error_name_required');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('quote_modal_error_email_required');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = t('quote_modal_error_email_invalid');
      }
    }
    
    // Empresa agora é obrigatória
    if (!formData.companyName.trim()) {
      newErrors.companyName = t('quote_modal_error_company_required');
    }
    
    if (!formData.description.trim()) {
      newErrors.description = t('quote_modal_error_description_required');
    } else if (formData.description.length > 1000) {
      newErrors.description = t('quote_modal_error_description_too_long');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // Clear previous API errors before new request
    setApiErrors([]);

    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        companyName: formData.companyName.trim(),
        description: formData.description.trim(),
        contactType: type,
      };

      const res = await postLandingContact(payload);
      // Optional: invoke parent callback after success
      if (onSubmit) {
        onSubmit(formData, type);
      }

      const defaultSuccess = isSupport
        ? t('quote_modal_success_support')
        : t('quote_modal_success_general');
      setSuccessMessage(res?.message ?? defaultSuccess);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
        handleClose();
      }, 2000);
    } catch (error) {
      // Try to parse backend error format
      const respData = (error as any)?.response?.data;
      const collected: string[] = [];

      if (respData) {
        if (Array.isArray(respData.errors)) {
          for (const item of respData.errors) {
            const msg = (item && (item.errorMessage || item.message)) as string | undefined;
            if (msg) {
              collected.push(msg);
            }
          }
        }
        if (respData.message && typeof respData.message === 'string') {
          // Include a top-level message if provided
          collected.push(respData.message);
        }
      }

      setApiErrors(collected.length ? collected : [t('quote_modal_error_generic')]);
      console.error('Error submitting quote request:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      companyName: '',
      description: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
            <BusinessIcon color="primary" />
            <Typography variant="h5" component="h2">
              {isSupport ? t('quote_modal_title_support') : t('quote_modal_title_budget')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {isSupport ? t('quote_modal_subtitle_support') : t('quote_modal_subtitle_budget')}
          </Typography>
        </DialogTitle>

        <DialogContent>
          {/* API error alerts displayed above the fields */}
          {apiErrors.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {apiErrors.map((msg, idx) => (
                <Alert key={idx} severity="error" sx={{ mb: 1 }}>
                  {msg}
                </Alert>
              ))}
            </Box>
          )}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid component={'div' as React.ElementType} item xs={12} md={4}
>
              <TextField
                fullWidth
                label={t('quote_modal_label_name')}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                variant="outlined"
                placeholder={t('quote_modal_placeholder_name')}
                inputProps={{ maxLength: 60 }}
              />
            </Grid>
            
            <Grid component={'div' as React.ElementType} item xs={12} md={4}
>
              <TextField
                fullWidth
                type="email"
                label={t('quote_modal_label_email')}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                variant="outlined"
                placeholder={t('quote_modal_placeholder_email')}
                inputProps={{ maxLength: 80 }}
              />
            </Grid>
            
            <Grid component={'div' as React.ElementType} item xs={12} md={4}
>
              <TextField
                fullWidth
                required
                label={t('quote_modal_label_company')}
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                error={!!errors.companyName}
                helperText={errors.companyName}
                variant="outlined"
                placeholder={t('quote_modal_placeholder_company')}
                inputProps={{ maxLength: 80 }}
              />
            </Grid>
            
            <Grid component={'div' as React.ElementType} item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={isSupport ? t('quote_modal_label_description_support') : t('quote_modal_label_description_budget')}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                error={!!errors.description}
                helperText={errors.description || (isSupport ? t('quote_modal_helper_description_support') : t('quote_modal_helper_description_budget'))}
                inputProps={{ maxLength: 1000 }}
                variant="outlined"
                placeholder={isSupport ? t('quote_modal_placeholder_description_support') : t('quote_modal_placeholder_description_budget')}
                style={{width: 715}}
// removed fixed width style
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleClose} color="inherit">
            {t('quote_modal_btn_cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name.trim() || !formData.email.trim() || !formData.companyName.trim() || !formData.description.trim() || formData.description.length > 1000}
            sx={{ minWidth: 140 }}
          >
            {t('quote_modal_btn_submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {successMessage || t('quote_modal_success_snackbar')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default QuoteModal;